import { useCallback, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';

import type { SearchInputStyledProps } from './SearchInput.types';
import type { InputProps } from '../Input/Input';

import * as T from '../../utils/toolbox';

import * as S from './SearchInput.styled';

interface SearchInputProps extends SearchInputStyledProps, InputProps {
  className?: string;
  placeholder?: string;
  autoFocus?: boolean;
  onChange: React.ChangeEventHandler<HTMLInputElement>;
}

const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  ({ className, onChange = T.noop, fullWidth, placeholder, ...inputProps }, ref) => {
    const { t } = useTranslation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const onChangeDebounced = useCallback(T.debounce(onChange, 250), [onChange]);

    const handleChange = useCallback<SearchInputProps['onChange']>(
      (event) => {
        event.persist();
        onChangeDebounced(event);
      },
      [onChangeDebounced],
    );

    const handleClear = useCallback(() => {
      onChange({ target: { value: '' } } as unknown as React.ChangeEvent<HTMLInputElement>);
    }, [onChange]);

    return (
      <S.Container role="search" fullWidth={fullWidth} className={className}>
        <S.TextInput
          fullWidth
          ref={ref}
          onChange={handleChange}
          placeholder={placeholder || t('searchInput.defaultPlaceholder', 'Search...')}
          name="search"
          aria-label="search"
          {...inputProps}
        />
        <S.SearchIcon />
        {typeof inputProps.value === 'string' && inputProps.value.length > 0 && (
          <S.ClearSearchButton onClick={handleClear} data-testid="SearchInput.ClearButton">
            <S.ClearSearchIcon />
          </S.ClearSearchButton>
        )}
      </S.Container>
    );
  },
);

SearchInput.displayName = 'SearchInput';

export default SearchInput;
